import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
  DocumentActionButtons,
  DocumentList,
  DocumentSuggestionsList,
  DocumentViewer,
  FolderSuggestionsCard,
  KeyTermsPanel,
  PropertySummaryCard,
  SuccessfulChecksList,
} from './components';
import {
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
  Participants,
} from './components/types';
import { useAgentComplianceReviewDocument } from './hooks/useAgentComplianceReviewDocument';
import { useAgentComplianceReviewIgnoreAndResubmit } from './hooks/useAgentComplianceReviewIgnoreAndResubmit';
import { useAgentComplianceReviewIgnoreSuggestion } from './hooks/useAgentComplianceReviewIgnoreSuggestion';
import { useAgentComplianceReviewKeyTerms } from './hooks/useAgentComplianceReviewKeyTerms';
import { useAgentComplianceReviewPlatformUrl } from './hooks/useAgentComplianceReviewPlatformUrl';
import { useAgentDocumentValidation } from './hooks/useAgentDocumentValidation';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface AgentComplianceReviewDesktopProps {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

interface RightRailProps {
  suggestions: ComplianceSuggestion[];
  successfulChecks: ComplianceSuccessfulCheck[];
  hasSelection: boolean;
  /**
   * True when the selected document has open suggestions. The document-level
   * action buttons (Ignore and resubmit / Fix in SkySlope) only show for
   * needs-review documents; clean documents have nothing to resubmit.
   */
  needsReview: boolean;
  loading: boolean;
  /** True until the per-document detail has loaded for the selection. */
  hasDetail: boolean;
  /** Called with a row's field ids when a suggestion/check row is clicked. */
  onSelectFields: (fieldIds: string[]) => void;
  /** Called with a suggestion to hide it (document-level ignore). */
  onIgnoreSuggestion: (suggestion: ComplianceSuggestion) => void;
  /** Called with an already-ignored suggestion to un-hide it (Undo). */
  onUndoIgnoreSuggestion: (suggestion: ComplianceSuggestion) => void;
  /** True while a document-level ignore mutation is in flight. */
  isIgnoring: boolean;
  /** Fires the resubmit action for the selected document with the agent's note. */
  onResubmit: (message: string) => void | Promise<void>;
  /**
   * The note the resubmit modal is prepopulated with — the crossed-out
   * (ignored) suggestion messages for the selected document.
   */
  defaultResubmitMessage: string;
  /** True once the selected document has already been resubmitted. */
  isResubmitted: boolean;
  /** True while the resubmit mutation is in flight. */
  isSubmitting: boolean;
  /** SkySlope (or other platform) deep link for the folder/document. */
  platformUrl?: string | null;
}

/**
 * Right rail: document-level suggestions + passing checks for the selected
 * document (derived client-side from the raw detail). Shows a spinner while the
 * detail loads and a muted placeholder when nothing is selected (or the
 * selected document has no findings).
 *
 * Clicking a suggestion/check row calls `onSelectFields(row.fieldIds)`, which
 * the page lifts into `activeFieldId` to scroll/center the PDF on the field.
 */
const RightRail: FC<RightRailProps> = ({
  suggestions,
  successfulChecks,
  hasSelection,
  needsReview,
  loading,
  hasDetail,
  onSelectFields,
  onIgnoreSuggestion,
  onUndoIgnoreSuggestion,
  isIgnoring,
  onResubmit,
  defaultResubmitMessage,
  isResubmitted,
  isSubmitting,
  platformUrl,
}) => {
  if (!hasSelection) {
    return (
      <Text color="compliance.text.muted" fontSize="sm">
        Select a document to see its suggestions and checks.
      </Text>
    );
  }

  const hasFindings = suggestions.length > 0 || successfulChecks.length > 0;

  return (
    <VStack align="stretch" spacing={5}>
      {/* Document-level actions sit above the suggestions (per Figma). The
          resubmit action shows for needs-review / already-resubmitted
          documents; the SkySlope link shows for any document (labeled "Fix" vs
          "Open" by status). DocumentActionButtons renders nothing when neither
          applies. */}
      <DocumentActionButtons
        defaultResubmitMessage={defaultResubmitMessage}
        isResubmitReady={hasDetail}
        // Gate only the resubmit trigger (not the overview-derived needsReview
        // signal / SkySlope label) on detail being loaded: the resubmit payload
        // is derived from detail (validation id + open suggestions), so the
        // modal must not open before that data exists.
        isResubmitted={isResubmitted}
        isSubmitting={isSubmitting}
        needsReview={needsReview}
        platformUrl={platformUrl}
        onResubmit={onResubmit}
      />

      {loading && !hasDetail ? (
        <Flex justify="center" py={8}>
          <Spinner color="compliance.loading.text" />
        </Flex>
      ) : hasFindings ? (
        <>
          <DocumentSuggestionsList
            isIgnoring={isIgnoring}
            suggestions={suggestions}
            onIgnore={onIgnoreSuggestion}
            onSelectFields={onSelectFields}
            onUndoIgnore={onUndoIgnoreSuggestion}
          />
          <SuccessfulChecksList
            checks={successfulChecks}
            onSelectFields={onSelectFields}
          />
        </>
      ) : (
        <Text color="compliance.text.muted" fontSize="sm">
          No suggestions or checks for this document.
        </Text>
      )}
    </VStack>
  );
};

/**
 * Desktop three-pane layout for the agent compliance review page.
 *
 * - **Left rail**: property summary, the key-terms panel, the folder-level
 *   suggestions card, and the selectable document list.
 * - **Center**: the {@link DocumentViewer} for the selected document (its own
 *   "select a document" empty state shows when nothing is selected).
 * - **Right rail**: document-level suggestions and passing checks.
 *
 * Selection is URL-driven: clicking a document navigates to
 * `compliance-review/:ingestionFolderUuid?token=…&documentId=…`, preserving the
 * `?token=` query param, and the per-document detail query fires off the
 * `documentId` query param. The mobile single-column layout and the responsive
 * breakpoint switch are handled separately (next task).
 */
export const AgentComplianceReviewDesktop: FC<
  AgentComplianceReviewDesktopProps
> = ({ overview, selectedDocumentId, token, ingestionFolderUuid }) => {
  const navigate = useNavigate();

  const { document, loading, error } = useAgentComplianceReviewDocument({
    token,
    ingestionFolderUuid,
    ingestionDocumentId: selectedDocumentId,
  });

  const { keyTerms } = useAgentComplianceReviewKeyTerms();

  // The platform deep-link ("Fix in SkySlope") is fetched ON DEMAND — only once
  // a document is open (the action buttons are shown). Resolving it calls the
  // SkySlope API, so keeping it off the overview means the page load and
  // post-mutation refetches never trigger that call. Skipped until a document
  // is selected.
  const { platformUrl } = useAgentComplianceReviewPlatformUrl({
    token,
    ingestionFolderUuid,
    skip: selectedDocumentId == null,
  });

  // The field id the user clicked a suggestion/check row to focus. Lifted here
  // so the right-rail lists and the center DocumentViewer stay in sync: the
  // viewer scrolls/centers the PDF on it and gives it the active outline.
  const [activeFieldId, setActiveFieldId] = useState<string | undefined>(
    undefined
  );

  // Clear the focused field when the selected document changes — a field id
  // from the previous document is meaningless against the new one.
  useEffect(() => {
    setActiveFieldId(undefined);
  }, [selectedDocumentId]);

  const onSelectFields = useCallback((fieldIds: string[]) => {
    setActiveFieldId(fieldIds[0]);
  }, []);

  const onSelectDocument = useCallback(
    (ingestionDocumentId: string) => {
      const search = createSearchParams({
        ...(token != null ? { token } : {}),
        documentId: ingestionDocumentId,
      }).toString();
      navigate(`/compliance-review/${ingestionFolderUuid}?${search}`);
    },
    [navigate, token, ingestionFolderUuid]
  );

  // Derive suggestions / successful checks / field highlights on the client
  // from the raw document detail — reusing the same shared compliance utils as
  // the drawer/extension, so the transformation isn't forked onto the backend.
  const validation = useAgentDocumentValidation(document);

  // Token-bound mutations. The hooks bind the share-link token + folder uuid;
  // handlers only supply the suggestion-identifying / document fields. The
  // hooks await a refetch of the overview + open detail, so the backend-derived
  // counts/state drive the UI (no local optimistic guess).
  const [ignore, { loading: isIgnoring }] =
    useAgentComplianceReviewIgnoreSuggestion({ token, ingestionFolderUuid });
  const [ignoreAndResubmit, { loading: isSubmitting }] =
    useAgentComplianceReviewIgnoreAndResubmit({ token, ingestionFolderUuid });

  const ingestionDocumentValidationId =
    document?.ingestionDocumentValidationId ?? undefined;

  // Hide a single document-level suggestion. Keyed on the selected document +
  // its validation report + the suggestion's owning contract document / rule.
  const onIgnoreDocumentSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignore({
        ingestionDocumentId: selectedDocumentId,
        ingestionDocumentValidationId,
        contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
        pageNumber: suggestion.pageNumbers?.[0],
      });
    },
    [ignore, selectedDocumentId, ingestionDocumentValidationId]
  );

  // Un-hide a previously AGENT-ignored document-level suggestion (Undo). Same
  // match keys as the ignore above, but `isHidden: false`.
  const onUndoIgnoreDocumentSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      if (selectedDocumentId == null) {
        return;
      }
      void ignore({
        ingestionDocumentId: selectedDocumentId,
        ingestionDocumentValidationId,
        contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
        pageNumber: suggestion.pageNumbers?.[0],
        isHidden: false,
      });
    },
    [ignore, selectedDocumentId, ingestionDocumentValidationId]
  );

  // Hide a single folder-level suggestion. Keyed on the folder validation id +
  // the document discriminators carried on the overview suggestion.
  const onIgnoreFolderSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      void ignore({
        ingestionFolderValidationId:
          suggestion.ingestionFolderValidationId ?? undefined,
        documentCategory: suggestion.documentCategory ?? undefined,
        documentForm: suggestion.documentForm ?? undefined,
        documentName: suggestion.documentName ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
      });
    },
    [ignore]
  );

  // Un-hide a previously ignored folder-level suggestion (Undo). Same match
  // keys as the folder ignore above, but `isHidden: false`.
  const onUndoIgnoreFolderSuggestion = useCallback(
    (suggestion: ComplianceSuggestion) => {
      void ignore({
        ingestionFolderValidationId:
          suggestion.ingestionFolderValidationId ?? undefined,
        documentCategory: suggestion.documentCategory ?? undefined,
        documentForm: suggestion.documentForm ?? undefined,
        documentName: suggestion.documentName ?? undefined,
        ruleUuid: suggestion.ruleUuid ?? undefined,
        isHidden: false,
      });
    },
    [ignore]
  );

  // The resubmit modal's note is prepopulated as a friendly message to the
  // reviewer: a greeting, then ALL of the document's flagged issues — whether
  // the agent has crossed them out or not (USER-suppressed suggestions are
  // already filtered out upstream and never appear here). Resubmitting declares
  // them all not relevant, so they're framed that way. The backend appends the
  // "– {Agent} via Indigo" signature, so it's not included here.
  const allSuggestionMessages = validation.suggestions.map(
    (suggestion) => suggestion.message
  );
  const defaultResubmitMessage = [
    "Hi, I've resubmitted this document for review.",
    ...(allSuggestionMessages.length > 0
      ? [
          `Here are the issues that I think are not relevant:\n${allSuggestionMessages
            .map((message) => `• ${message}`)
            .join('\n')}`,
        ]
      : []),
    'Thanks!',
  ].join('\n\n');

  // Resubmit the selected document with the agent's (possibly edited) note.
  // Clicking resubmit also ignores every still-open suggestion (the ones the
  // agent had NOT already crossed out) — already-ignored suggestions stay
  // hidden and are not re-sent. The backend hides them, posts the note to
  // SkySlope, and sets the item back to Pending. Once it lands, we auto-advance
  // to the next checklist item that still needs review so the agent can work
  // straight down the list.
  const onResubmit = useCallback(
    async (message: string) => {
      // Guard on the detail-derived validation id too: it (and the suggestion
      // list below) only exist once the per-document detail has loaded, so
      // firing before then would send an incomplete ignore payload.
      if (selectedDocumentId == null || ingestionDocumentValidationId == null) {
        return;
      }
      const suggestionsToIgnore = validation.suggestions
        .filter((suggestion) => !suggestion.isIgnored)
        .map((suggestion) => ({
          contractDocumentUuid: suggestion.contractDocumentUuid ?? undefined,
          ruleUuid: suggestion.ruleUuid ?? undefined,
          pageNumber: suggestion.pageNumbers?.[0],
        }));

      // Resolve the next item to advance to BEFORE the resubmit (the whole
      // checklist activity becomes resubmitted, so skip its documents): the
      // first remaining document — in a different activity — that needs review.
      const currentActivityId =
        overview.documents.find(
          (doc) => doc.ingestionDocumentId === selectedDocumentId
        )?.skyslopeActivityId ?? null;
      const nextDocument = overview.documents.find((doc) => {
        if (doc.ingestionDocumentId === selectedDocumentId) {
          return false;
        }
        if (
          currentActivityId != null &&
          doc.skyslopeActivityId === currentActivityId
        ) {
          return false;
        }

        return doc.openSuggestionCount > 0 && doc.agentResubmittedAt == null;
      });

      await ignoreAndResubmit({
        ingestionDocumentId: selectedDocumentId,
        ingestionDocumentValidationId,
        message,
        suggestionsToIgnore,
      });

      if (nextDocument != null) {
        onSelectDocument(nextDocument.ingestionDocumentId);
      }
    },
    [
      ignoreAndResubmit,
      selectedDocumentId,
      ingestionDocumentValidationId,
      validation.suggestions,
      overview.documents,
      onSelectDocument,
    ]
  );

  const hasSelection = selectedDocumentId != null;

  // The selected document's summary row carries `agentResubmittedAt` — once set
  // the resubmit action is spent (disabled + relabeled) — and `openSuggestionCount`,
  // which gates the document-level action buttons (only needs-review documents
  // can be resubmitted).
  const selectedDocSummary = overview.documents.find(
    (doc) => doc.ingestionDocumentId === selectedDocumentId
  );
  // A resubmitted document reads as "no issues" — its open count no longer makes
  // it need review (the resubmit action becomes the disabled "Resubmitted").
  const selectedDocNeedsReview =
    (selectedDocSummary?.openSuggestionCount ?? 0) > 0 &&
    selectedDocSummary?.agentResubmittedAt == null;

  return (
    <Grid
      bg="compliance.background.primary"
      gap={0}
      h="100dvh"
      maxH="100dvh"
      // Single full-height row that can shrink below its content (minmax 0)
      // so each column scrolls independently instead of growing the page.
      overflow="hidden"
      templateColumns={{ base: '280px 1fr', xl: '320px 1fr 340px' }}
      templateRows="minmax(0, 1fr)"
      w="full"
    >
      {/* Left rail */}
      <GridItem
        bg="compliance.sidebar.bg"
        borderRightColor="compliance.sidebar.border"
        borderRightWidth={1}
        h="full"
        minH={0}
        overflowY="auto"
      >
        <VStack align="stretch" p={4} spacing={5}>
          <Box>
            <Text
              color="compliance.text.muted"
              fontSize="xs"
              fontWeight="medium"
              letterSpacing="wide"
              textTransform="uppercase"
            >
              Compliance review
            </Text>
            <Heading
              color="compliance.text.primary"
              fontSize="lg"
              mt={1}
              size="headline"
            >
              {overview.folderName}
            </Heading>
          </Box>

          <PropertySummaryCard
            folderName={overview.folderName}
            property={overview.property}
          />

          <KeyTermsPanel
            keyTerms={keyTerms}
            participants={
              overview.participants as Participants | null | undefined
            }
          />

          <FolderSuggestionsCard
            isIgnoring={isIgnoring}
            suggestions={overview.folderSuggestions}
            onIgnore={onIgnoreFolderSuggestion}
            onUndoIgnore={onUndoIgnoreFolderSuggestion}
          />

          <DocumentList
            documents={overview.documents}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={onSelectDocument}
          />
        </VStack>
      </GridItem>

      {/* Center pane: PDF viewer */}
      <GridItem h="full" minH={0} minWidth={0} overflow="hidden">
        {error ? (
          <Flex
            align="center"
            bg="compliance.pdf.bg"
            h="full"
            justify="center"
            p={6}
            w="full"
          >
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document. Please try again.
            </Text>
          </Flex>
        ) : (
          <DocumentViewer
            activeFieldId={activeFieldId}
            documentName={document?.documentName}
            loading={loading}
            pdfUrl={document?.pdfUrl}
            submittedAt={
              document?.submittedAt != null
                ? String(document.submittedAt)
                : undefined
            }
            successFieldIds={validation.successFieldIds}
            validationFieldsMap={validation.validationFieldsMap}
            warningFieldIds={validation.errorFieldIds}
            warningFieldMessages={validation.fieldMessages}
          />
        )}
      </GridItem>

      {/* Right rail: suggestions + checks for the selected document */}
      <GridItem
        bg="compliance.sidebar.bg"
        borderLeftColor="compliance.sidebar.border"
        borderLeftWidth={1}
        display={{ base: 'none', xl: 'block' }}
        h="full"
        minH={0}
        overflowY="auto"
      >
        <Box p={4}>
          {error ? (
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document.
            </Text>
          ) : (
            <RightRail
              defaultResubmitMessage={defaultResubmitMessage}
              hasDetail={document != null}
              hasSelection={hasSelection}
              isIgnoring={isIgnoring}
              isResubmitted={selectedDocSummary?.agentResubmittedAt != null}
              isSubmitting={isSubmitting}
              loading={loading}
              needsReview={selectedDocNeedsReview}
              platformUrl={platformUrl}
              successfulChecks={validation.successfulChecks}
              suggestions={validation.suggestions}
              onIgnoreSuggestion={onIgnoreDocumentSuggestion}
              onResubmit={onResubmit}
              onSelectFields={onSelectFields}
              onUndoIgnoreSuggestion={onUndoIgnoreDocumentSuggestion}
            />
          )}
        </Box>
      </GridItem>
    </Grid>
  );
};
